import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, ArrowRight, Trophy, Target, Zap, Flame, Clock, CheckCircle2, Crown, Heart, UserPlus, Users as UsersIcon, X } from "lucide-react";
import Avatar from "../components/Avatar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { TeamCard } from "../components/PlayerCard";
import { TierChip } from "./Leaderboards";
import { RankedTierChip } from "./Ranked";
import { Logo } from "../components/Logo";
import NavBell from "../components/NavBell";
import { onPersonalEvent } from "../hooks/usePersonalSocket";
import { countryFlag, fmtMs } from "../lib/derive";
import { toast } from "sonner";
import { UserName } from "../components/UserName";

function StatTile({ icon: Icon, label, value, suffix = "", testId, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[var(--surface-2)] to-[var(--surface)] p-4"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <Icon size={14} className="text-[var(--accent)]/70" />
      </div>
      <div className="mt-2 text-3xl font-black tabular-nums" data-testid={`stat-${testId}`}>
        {value}
        {suffix && <span className="text-base text-zinc-500 font-bold ml-1">{suffix}</span>}
      </div>
      <div className="mt-2 h-1 w-full rounded-full bg-white/5 overflow-hidden">
        <div className="h-full w-3/4 bg-gradient-to-r from-[var(--accent)]/40 to-transparent" />
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [recent, setRecent] = useState([]);
  const [rank, setRank] = useState(null);
  const [onlineFriends, setOnlineFriends] = useState([]);
  const [invitePopoverFor, setInvitePopoverFor] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  // Demo-only: ?demo_toast=achievement renders the sticky achievement toast so
  // QA/screenshot tooling can capture it without playing a full game.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("demo_toast") === "achievement") {
      setTimeout(() => {
        toast.success("Insane Winner", {
          description: "Win a game on Insane difficulty",
          duration: 8000,
          id: "ach-demo",
          className: "achievement-toast",
        });
      }, 400);
    }
    if (sp.get("demo_toast") === "lobby_invite") {
      setTimeout(() => {
        toast.message("bob invited you to a Medium Classic lobby", {
          description: "Code ABC123 · 5 rounds · 3/4 players",
          duration: 12000,
          id: "inv-demo",
        });
      }, 400);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadOnline = async () => {
      try {
        const { data } = await api.get("/friends?status=accepted&limit=200");
        if (cancelled) return;
        setOnlineFriends((data.items || []).filter((r) => r.user.is_online));
      } catch { /* */ }
    };
    (async () => {
      try {
        const [meRes, mineRes, rankRes] = await Promise.all([
          api.get("/me"),
          api.get("/lobbies/mine"),
          api.get("/leaderboards/me").catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setMe(meRes.data);
          setRecent(mineRes.data.lobbies || []);
          setRank(rankRes.data);
        }
        await loadOnline();
      } catch {
        // ignore
      }
    })();
    const off = onPersonalEvent((evt) => {
      if (evt.type === "friend_status_changed") loadOnline();
    });
    return () => { cancelled = true; off(); };
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const stats = me?.user_stats || {
    games_played: 0, wins: 0, total_correct: 0, total_answers: 0,
    avg_response_ms: 0, best_streak: 0, current_streak: 0, football_iq: 0,
  };
  const winRate = stats.games_played ? Math.round((stats.wins / stats.games_played) * 100) : 0;
  const accuracy = stats.total_answers ? Math.round((stats.total_correct / stats.total_answers) * 100) : 0;
  const flag = countryFlag(me?.user?.country);

  const joinByCode = async (e) => {
    e?.preventDefault();
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      toast.error("Code must be 6 characters");
      return;
    }
    setJoining(true);
    try {
      await api.post(`/lobbies/${code}/join`);
      navigate(`/lobby/${code}`);
    } catch (err) {
      const s = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (s === 404) toast.error("Lobby not found");
      else toast.error(detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  const initials = (user?.username || "?")[0]?.toUpperCase();

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="px-6 py-5 max-w-6xl mx-auto flex items-center justify-between">
        <Logo to="/dashboard" size={36} />
        <nav className="flex items-center gap-2 text-sm">
          <Link to="/ranked" data-testid="nav-ranked" className="px-4 py-2 rounded-full hover:bg-white/5 transition text-[var(--accent)]">Ranked</Link>
          <Link to="/leaderboards" data-testid="nav-leaderboards" className="px-4 py-2 rounded-full hover:bg-white/5 transition">Leaderboards</Link>
          <Link to="/profile" data-testid="nav-profile" className="px-4 py-2 rounded-full hover:bg-white/5 transition">Profile</Link>
          <Link to="/friends" data-testid="nav-friends" className="px-4 py-2 rounded-full hover:bg-white/5 transition">Friends</Link>
          <Link to="/store" data-testid="nav-store" className="px-4 py-2 rounded-full hover:bg-emerald-500/10 transition text-emerald-300 inline-flex items-center gap-1.5">
            <Heart size={12} fill="currentColor" /> Donate
          </Link>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin"
              className="px-4 py-2 rounded-full text-[var(--accent)] hover:bg-[var(--accent)]/10 transition">
              Admin
            </Link>
          )}
          <NavBell />
          <button onClick={onLogout} data-testid="btn-logout" className="px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 transition">
            Log out
          </button>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        {/* HERO */}
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
          <div className="flex items-center gap-5 flex-1">
            <div className="relative">
              <Avatar user={user} size={80} />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">welcome back</div>
              <h1 className="text-3xl sm:text-4xl font-bold mt-1.5" data-testid="dashboard-greeting">
                Hey, {me?.user ? <UserName user={me.user} /> : (user?.username || "player")}.
              </h1>
              <div className="text-sm text-zinc-500 mt-1 flex flex-wrap items-center gap-2">
                <span>Football IQ <span className="text-[var(--accent)] font-bold">{stats.football_iq}</span></span>
                {(() => {
                  const best = me?.ranked && [me.ranked.classic, me.ranked.career_path, me.ranked.quiz]
                    .filter(b => (b?.games_played || 0) > 0)
                    .sort((a, b) => (b.mmr || 0) - (a.mmr || 0))[0];
                  return best ? <RankedTierChip tier={best.tier} mmr={best.mmr} /> : null;
                })()}
                {me?.user?.role === "admin" && <span className="ml-1 px-2 py-0.5 rounded-full bg-white/5 text-[10px] uppercase tracking-widest">admin</span>}
              </div>
            </div>
          </div>
          {me?.favorite_club && (
            <div className="w-full lg:w-72">
              <TeamCard team={me.favorite_club} testId="dash-favourite-club" />
            </div>
          )}
        </motion.section>

        {/* CTAs */}
        <section className="grid lg:grid-cols-3 gap-4">
          <Link
            to="/lobby/new"
            data-testid="dash-create-lobby"
            className="group relative overflow-hidden rounded-2xl bg-[var(--accent)] text-black p-6 flex items-center gap-5 hover:brightness-110 transition shadow-[0_10px_40px_-10px_var(--accent)]"
          >
            <div className="w-14 h-14 rounded-xl bg-black/15 flex items-center justify-center">
              <Plus size={28} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">host</div>
              <div className="text-2xl font-black">Create a lobby</div>
              <div className="text-xs opacity-70 mt-0.5">Pick difficulty, rounds, players. Share the code.</div>
            </div>
            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link
            to="/solo"
            data-testid="dash-solo-cta"
            className="group relative overflow-hidden rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-950 to-black p-6 flex items-center gap-5 hover:border-emerald-400 transition"
          >
            <div className="w-14 h-14 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-300">
              <Heart size={28} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">single-player</div>
              <div className="text-2xl font-black">Football Islands</div>
              <div data-testid="dash-solo-progress" className="text-xs text-zinc-400 mt-0.5">
                Level <span className="text-emerald-300 font-bold">{(me?.solo_max_unlocked_level || 1)}</span> / 100
                · <span className="text-rose-300">{(me?.solo_lives ?? 5)}/5</span> ❤
              </div>
            </div>
            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform text-emerald-300" />
          </Link>

          <Link
            to="/ranked"
            data-testid="dash-ranked-cta"
            className="group relative overflow-hidden rounded-2xl border border-[var(--accent)]/40 bg-gradient-to-br from-purple-900/30 to-black p-6 flex items-center gap-5 hover:border-[var(--accent)] transition"
          >
            <div className="w-14 h-14 rounded-xl bg-[var(--accent)]/15 flex items-center justify-center text-[var(--accent)]">
              <Crown size={28} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)]">competitive</div>
              <div className="text-2xl font-black">Ranked</div>
              {(() => {
                const cgp = stats.games_played || 0;
                const totalRanked =
                  (me?.ranked?.classic?.games_played || 0) +
                  (me?.ranked?.career_path?.games_played || 0);
                if (cgp < 10) {
                  const left = 10 - cgp;
                  const pct = Math.round((cgp / 10) * 100);
                  return (
                    <>
                      <div data-testid="dash-ranked-progress" className="text-xs text-zinc-400 mt-0.5">
                        Unlocks in {left} more casual game{left === 1 ? "" : "s"}
                      </div>
                      <div className="mt-2 h-1 w-32 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
                      </div>
                    </>
                  );
                }
                if (totalRanked === 0) {
                  return <div data-testid="dash-ranked-ready" className="text-xs text-emerald-300 mt-0.5">Ready for Ranked — try your first match</div>;
                }
                return <div className="text-xs text-zinc-400 mt-0.5">Climb 8 tiers. FFA Elo.</div>;
              })()}
            </div>
            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform text-[var(--accent)]" />
          </Link>

          <form onSubmit={joinByCode} className="rounded-2xl border border-white/10 bg-[var(--surface)] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)]">join</div>
            <div className="text-2xl font-black mt-1">Got a code?</div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                data-testid="dash-join-code"
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                placeholder="ABC123"
                maxLength={6}
                className="flex-1 px-4 py-3 rounded-xl bg-black border border-white/10 focus:border-[var(--accent)] outline-none font-mono text-center tracking-[0.4em] uppercase transition"
              />
              <button
                type="submit"
                disabled={joining || joinCode.length !== 6}
                data-testid="dash-join-submit"
                className="px-5 py-3 rounded-xl bg-[var(--accent)] text-black font-bold hover:brightness-110 disabled:opacity-30 transition"
              >
                {joining ? "joining…" : "Join"}
              </button>
            </div>
          </form>
        </section>

        {/* Stat tiles */}
        <section>
          <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">your stats</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <StatTile icon={Trophy} label="Games" value={stats.games_played} testId="games-played" delay={0.0} />
            <StatTile icon={Trophy} label="Win rate" value={winRate} suffix="%" testId="wins" delay={0.05} />
            <StatTile icon={Target} label="Accuracy" value={accuracy} suffix="%" testId="accuracy" delay={0.1} />
            <StatTile icon={Flame} label="Best streak" value={stats.best_streak} testId="best-streak" delay={0.15} />
            <StatTile icon={Zap} label="Current streak" value={stats.current_streak} testId="current-streak" delay={0.2} />
            <StatTile icon={Clock} label="Avg time" value={fmtMs(stats.avg_response_ms)} testId="avg-time" delay={0.25} />
          </div>
        </section>

        {/* Friends online (Phase 5.4) */}
        <section data-testid="friends-online-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-500 inline-flex items-center gap-2">
              <UsersIcon size={12} /> Friends online
            </h2>
            <Link to="/friends" className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300">
              View all →
            </Link>
          </div>
          {onlineFriends.length === 0 ? (
            <div className="text-sm text-zinc-500 px-5 py-8 rounded-xl border border-white/5 bg-zinc-950/60 text-center">
              No friends online right now.{" "}
              <Link to="/friends" className="text-[var(--accent)] underline-offset-2 hover:underline">
                Browse friends
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {onlineFriends.slice(0, 6).map((row) => {
                const u = row.user;
                const myWaiting = recent.filter((l) => l.status === "waiting");
                const onInvite = async (lobbyCode) => {
                  try {
                    await api.post(`/lobbies/${lobbyCode}/invite-friend`, { to_user_id: u.user_id });
                    toast.success(`Invited ${u.username}`);
                    setInvitePopoverFor(null);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Invite failed");
                  }
                };
                return (
                  <div key={u.user_id}
                       data-testid={`friend-online-${u.user_id}`}
                       className="relative flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/5 bg-zinc-950/70 hover:border-white/10 transition">
                    <Link to={`/users/${u.user_id}`} className="flex items-center gap-3 min-w-0">
                      <div className="relative shrink-0">
                        <Avatar user={u} size="md" />
                        <span className="absolute -bottom-0.5 -left-0.5 size-2.5 rounded-full bg-emerald-500 ring-2 ring-zinc-950" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate flex items-center gap-1.5">
                          <UserName user={u} /><span className="text-[10px]">{countryFlag(u.country)}</span>
                        </div>
                      </div>
                    </Link>

                    {myWaiting.length === 0 ? (
                      <button
                        data-testid={`friend-online-invite-${u.user_id}`}
                        onClick={() => navigate(`/lobby/new?invite=${u.user_id}`)}
                        className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] uppercase tracking-widest hover:bg-white/10 inline-flex items-center gap-1.5"
                      >
                        <Plus size={11}/> Create &amp; invite
                      </button>
                    ) : (
                      <>
                        <button
                          data-testid={`friend-online-invite-${u.user_id}`}
                          onClick={() => setInvitePopoverFor(invitePopoverFor === u.user_id ? null : u.user_id)}
                          className="px-3 py-1.5 rounded-lg bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[11px] uppercase tracking-widest text-[var(--accent)] hover:bg-[var(--accent)]/25 inline-flex items-center gap-1.5"
                        >
                          <UserPlus size={11}/> Invite
                        </button>
                        {invitePopoverFor === u.user_id && (
                          <div className="absolute right-3 top-full mt-1 z-20 w-56 rounded-lg border border-white/10 bg-zinc-950 shadow-xl shadow-black/60 p-1">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-2 py-1">Pick a lobby</div>
                            {myWaiting.map((l) => (
                              <button key={l.lobby_code}
                                      onClick={() => onInvite(l.lobby_code)}
                                      className="w-full text-left px-2 py-1.5 rounded-md hover:bg-white/5 text-xs flex items-center justify-between">
                                <span className="font-mono">{l.lobby_code}</span>
                                <span className="text-[10px] text-zinc-500">{l.settings?.game_mode} {l.settings?.difficulty}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Recent lobbies */}
        {recent.length > 0 && (
          <section>
            <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">your active lobbies</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recent.slice(0, 6).map((l) => {
                const isHost = l.host_user_id === user?.id;
                const isRanked = !!l.is_ranked;
                const onClose = async (e) => {
                  e.preventDefault(); e.stopPropagation();
                  const label = isHost && !isRanked ? "Disband" : "Leave";
                  const msg = isHost && !isRanked
                    ? `Disband lobby ${l.lobby_code}? All players will be returned to dashboard.`
                    : `Leave lobby ${l.lobby_code}?`;
                  // eslint-disable-next-line no-alert
                  if (!window.confirm(msg)) return;
                  try {
                    if (isHost && !isRanked) {
                      await api.post(`/lobbies/${l.lobby_code}/disband`);
                      toast.success("Lobby disbanded");
                    } else {
                      await api.post(`/lobbies/${l.lobby_code}/leave`);
                      toast.success("You left the lobby");
                    }
                    setRecent((prev) => prev.filter((x) => x.id !== l.id));
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || `${label} failed`);
                  }
                };
                return (
                  <div key={l.id} className="relative">
                    <Link
                      to={`/lobby/${l.lobby_code}`}
                      data-testid={`dash-recent-${l.lobby_code}`}
                      className="block rounded-xl border border-white/10 bg-[var(--surface)] p-4 pr-10 hover:border-[var(--accent)]/40 transition group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-mono font-black tracking-[0.2em] text-xl">{l.lobby_code}</div>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${l.status === "waiting" ? "bg-[var(--accent)]/10 text-[var(--accent)] border-[var(--accent)]/30" : "bg-amber-400/10 text-amber-300 border-amber-400/30"}`}>
                          {l.status}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-2 flex items-center gap-2">
                        <CheckCircle2 size={12} />
                        {l.player_count}/{l.max_players} · {l.settings.difficulty}
                      </div>
                    </Link>
                    <button
                      type="button"
                      onClick={onClose}
                      data-testid={`dash-recent-close-${l.lobby_code}`}
                      title={isHost && !isRanked ? "Disband lobby" : "Leave lobby"}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 hover:bg-red-500/20 border border-white/10 hover:border-red-500/60 flex items-center justify-center text-zinc-400 hover:text-red-300 transition"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
